import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Search, X, Users, UserCircle, Truck, Receipt, FileText, ShoppingCart, DollarSign, Loader2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_ICONS = {
  employees: Users,
  clients: UserCircle,
  suppliers: Truck,
  invoices: Receipt,
  quotations: FileText,
  purchase_orders: ShoppingCart,
  vouchers: DollarSign
};

const CATEGORY_COLORS = {
  employees: 'bg-blue-100 text-blue-700',
  clients: 'bg-green-100 text-green-700',
  suppliers: 'bg-orange-100 text-orange-700',
  invoices: 'bg-purple-100 text-purple-700',
  quotations: 'bg-amber-100 text-amber-700',
  purchase_orders: 'bg-teal-100 text-teal-700',
  vouchers: 'bg-pink-100 text-pink-700'
};

export default function UniversalSearch() {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const searchRef = useRef(null);
  const inputRef = useRef(null);
  const navigate = useNavigate();
  
  // Get token (matching AuthContext key)
  const getToken = () => localStorage.getItem('erp_token');
  
  // Debounced search
  const searchDebounceRef = useRef(null);
  
  const fetchSuggestions = useCallback(async (searchQuery) => {
    if (!searchQuery || searchQuery.length < 1) {
      setSuggestions([]);
      return;
    }
    
    try {
      const response = await fetch(`${API_URL}/api/search/suggestions?q=${encodeURIComponent(searchQuery)}`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setSuggestions(data.suggestions || []);
      }
    } catch (error) {
      console.error('Error fetching suggestions:', error);
    }
  }, []);
  
  const performFullSearch = useCallback(async (searchQuery) => {
    if (!searchQuery || searchQuery.length < 1) {
      setResults(null);
      return;
    }
    
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/search?q=${encodeURIComponent(searchQuery)}&limit=10`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setResults(data);
      }
    } catch (error) {
      console.error('Error performing search:', error);
    } finally {
      setLoading(false);
    }
  }, []);
  
  useEffect(() => {
    if (query.length >= 1) {
      // Debounce suggestions
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }
      searchDebounceRef.current = setTimeout(() => {
        fetchSuggestions(query);
      }, 150);
    } else {
      setSuggestions([]);
      setResults(null);
    }
    
    return () => {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }
    };
  }, [query, fetchSuggestions]);
  
  // Handle Enter key for full search
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && query.length >= 1) {
      e.preventDefault();
      performFullSearch(query);
      setSelectedIndex(-1);
    } else if (e.key === 'Escape') {
      setIsOpen(false);
      setQuery('');
      setResults(null);
      setSuggestions([]);
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      const totalItems = suggestions.length;
      if (totalItems > 0) {
        setSelectedIndex(prev => (prev + 1) % totalItems);
      }
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      const totalItems = suggestions.length;
      if (totalItems > 0) {
        setSelectedIndex(prev => (prev - 1 + totalItems) % totalItems);
      }
    }
  };
  
  const handleSuggestionClick = (suggestion) => {
    if (suggestion.link) {
      navigate(suggestion.link);
      setIsOpen(false);
      setQuery('');
      setResults(null);
      setSuggestions([]);
    }
  };
  
  const handleResultClick = (item) => {
    if (item.link) {
      navigate(item.link);
      setIsOpen(false);
      setQuery('');
      setResults(null);
      setSuggestions([]);
    }
  };
  
  // Click outside to close
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  
  // Keyboard shortcut (Cmd+K or Ctrl+K)
  useEffect(() => {
    const handleKeyboardShortcut = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setIsOpen(true);
        setTimeout(() => inputRef.current?.focus(), 100);
      }
    };
    
    document.addEventListener('keydown', handleKeyboardShortcut);
    return () => document.removeEventListener('keydown', handleKeyboardShortcut);
  }, []);
  
  // Focus input when opened
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);
  
  const getIconComponent = (iconName) => {
    const icons = { Users, UserCircle, Truck, Receipt, FileText, ShoppingCart, DollarSign };
    return icons[iconName] || Search;
  };
  
  return (
    <div className="relative" ref={searchRef}>
      {/* Search Trigger Button */}
      <button
        onClick={() => setIsOpen(true)}
        className="flex items-center gap-2 px-3 py-2 text-sm text-muted-foreground bg-muted hover:bg-muted/80 rounded-lg transition-colors border border-border"
        data-testid="universal-search-trigger"
      >
        <Search className="w-4 h-4" />
        <span className="hidden sm:inline">Search...</span>
        <kbd className="hidden sm:inline-flex items-center gap-1 px-1.5 py-0.5 text-xs font-medium text-muted-foreground bg-background rounded border border-border">
          <span className="text-xs">⌘</span>K
        </kbd>
      </button>
      
      {/* Search Modal/Dropdown - Portal to escape topbar stacking context */}
      {isOpen && createPortal(
        <div className="fixed inset-0 z-[100] overflow-y-auto" data-testid="universal-search-modal">
          {/* Backdrop */}
          <div className="fixed inset-0 bg-black/50 transition-opacity" onClick={() => setIsOpen(false)} />
          
          {/* Search Panel */}
          <div className="relative flex items-start justify-center pt-20 px-4">
            <div className="relative w-full max-w-2xl bg-background rounded-xl shadow-2xl border border-border" style={{ maxHeight: '75vh', display: 'flex', flexDirection: 'column' }}>
              {/* Search Input */}
              <div className="flex items-center px-4 border-b border-border shrink-0">
                <Search className="w-5 h-5 text-muted-foreground" />
                <input
                  ref={inputRef}
                  type="text"
                  placeholder="Search employees, clients, invoices, POs..."
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  onKeyDown={handleKeyDown}
                  className="flex-1 px-4 py-4 text-base text-foreground placeholder:text-muted-foreground outline-none bg-transparent"
                  data-testid="universal-search-input"
                />
                {query && (
                  <button
                    onClick={() => {
                      setQuery('');
                      setResults(null);
                      setSuggestions([]);
                    }}
                    className="p-1 hover:bg-muted rounded"
                  >
                    <X className="w-4 h-4 text-muted-foreground" />
                  </button>
                )}
                {loading && <Loader2 className="w-5 h-5 text-primary animate-spin ml-2" />}
              </div>
              
              {/* Results Area */}
              <div className="overflow-y-auto" style={{ maxHeight: 'calc(75vh - 120px)' }}>
                {/* Quick Suggestions */}
                {suggestions.length > 0 && !results && (
                  <div className="p-2">
                    <div className="text-xs font-medium text-muted-foreground px-3 py-2">Quick Results</div>
                    {suggestions.map((suggestion, index) => {
                      const IconComponent = getIconComponent(suggestion.icon);
                      return (
                        <button
                          key={`${suggestion.type}-${index}`}
                          onClick={() => handleSuggestionClick(suggestion)}
                          className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left transition-colors ${
                            selectedIndex === index ? 'bg-primary/10' : 'hover:bg-muted'
                          }`}
                          data-testid={`search-suggestion-${index}`}
                        >
                          <div className={`p-1.5 rounded-lg ${CATEGORY_COLORS[suggestion.type] || 'bg-muted text-muted-foreground'}`}>
                            <IconComponent className="w-4 h-4" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-foreground truncate">{suggestion.label}</div>
                            {suggestion.secondary && (
                              <div className="text-xs text-muted-foreground truncate">{suggestion.secondary}</div>
                            )}
                          </div>
                        </button>
                      );
                    })}
                    <div className="text-xs text-center text-muted-foreground py-2">
                      Press Enter for full search
                    </div>
                  </div>
                )}
                
                {/* Full Search Results */}
                {results && results.categories && Object.keys(results.categories).length > 0 && (
                  <div className="p-2">
                    <div className="text-xs font-medium text-muted-foreground px-3 py-2">
                      Found {results.total_count} results for "{results.query}"
                    </div>
                    
                    {Object.entries(results.categories).map(([categoryKey, category]) => {
                      const IconComponent = CATEGORY_ICONS[categoryKey] || Search;
                      return (
                        <div key={categoryKey} className="mb-4">
                          <div className="flex items-center gap-2 px-3 py-2">
                            <IconComponent className="w-4 h-4 text-muted-foreground" />
                            <span className="text-sm font-semibold text-foreground">{category.label}</span>
                            <span className="text-xs text-muted-foreground">({category.count})</span>
                          </div>
                          
                          {category.items.map((item, idx) => (
                            <button
                              key={item.id || idx}
                              onClick={() => handleResultClick(item)}
                              className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left hover:bg-muted transition-colors"
                              data-testid={`search-result-${categoryKey}-${idx}`}
                            >
                              <div className={`p-1.5 rounded-lg ${CATEGORY_COLORS[categoryKey] || 'bg-muted'}`}>
                                <IconComponent className="w-4 h-4" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="font-medium text-foreground truncate">{item.title}</div>
                                <div className="text-xs text-muted-foreground flex items-center gap-2">
                                  {item.subtitle && <span className="truncate">{item.subtitle}</span>}
                                  {item.description && (
                                    <>
                                      <span>&#8226;</span>
                                      <span className="truncate">{item.description}</span>
                                    </>
                                  )}
                                </div>
                              </div>
                              {item.status && (
                                <span className={`text-xs px-2 py-0.5 rounded-full ${
                                  item.status === 'approved' || item.status === 'paid' ? 'bg-green-100 text-green-700' :
                                  item.status === 'draft' ? 'bg-gray-100 text-gray-600' :
                                  item.status === 'sent' ? 'bg-blue-100 text-blue-700' :
                                  'bg-gray-100 text-gray-600'
                                }`}>
                                  {item.status}
                                </span>
                              )}
                            </button>
                          ))}
                        </div>
                      );
                    })}
                  </div>
                )}
                
                {/* No Results */}
                {results && results.total_count === 0 && (
                  <div className="p-8 text-center">
                    <Search className="w-12 h-12 text-muted-foreground/30 mx-auto mb-3" />
                    <p className="text-muted-foreground">No results found for "{results.query}"</p>
                    <p className="text-sm text-muted-foreground/60 mt-1">Try a different search term</p>
                  </div>
                )}
                
                {/* Empty State */}
                {!query && !results && suggestions.length === 0 && (
                  <div className="p-8 text-center">
                    <div className="text-muted-foreground mb-4">
                      <Search className="w-12 h-12 mx-auto mb-3 opacity-50" />
                      <p className="font-medium">Search across your business</p>
                    </div>
                    <div className="grid grid-cols-2 gap-2 max-w-sm mx-auto text-xs text-muted-foreground">
                      <div className="flex items-center gap-2 p-2 bg-muted rounded-lg">
                        <Users className="w-4 h-4" /> Employees
                      </div>
                      <div className="flex items-center gap-2 p-2 bg-muted rounded-lg">
                        <UserCircle className="w-4 h-4" /> Clients
                      </div>
                      <div className="flex items-center gap-2 p-2 bg-muted rounded-lg">
                        <Truck className="w-4 h-4" /> Suppliers
                      </div>
                      <div className="flex items-center gap-2 p-2 bg-muted rounded-lg">
                        <Receipt className="w-4 h-4" /> Invoices
                      </div>
                    </div>
                  </div>
                )}
              </div>
              
              {/* Footer */}
              <div className="px-4 py-2 border-t border-border bg-muted/50 text-xs text-muted-foreground flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <span className="flex items-center gap-1">
                    <kbd className="px-1.5 py-0.5 bg-background rounded border border-border">↑↓</kbd> Navigate
                  </span>
                  <span className="flex items-center gap-1">
                    <kbd className="px-1.5 py-0.5 bg-background rounded border border-border">Enter</kbd> Search
                  </span>
                  <span className="flex items-center gap-1">
                    <kbd className="px-1.5 py-0.5 bg-background rounded border border-border">Esc</kbd> Close
                  </span>
                </div>
                <span>BizCore Universal Search</span>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
